import banner from '@images/request_banner/image.jpg'
import { ChevronDownIcon, CloseIcon, PlusIcon } from '@svgs'
import { FC, useEffect, useRef, useState, WheelEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, Checkbox, InputField, MySelect } from 'src/components/ui'
import MyModal, { IMyModal } from 'src/components/ui/modal/MyModal'
import SuccessModal from '../success/SuccessModal'
// import { PropertyTypeSchema } from '@backend/zod'
import { usePropertyOptions } from '@hooks/usePropertyOptions'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { AutocompleteCustom } from 'src/components/maps/AutocompleteCustom'
import {
  useCreatePropertySearchMutation,
  useGetPropertySearchByIdQuery,
  usePlaceDetailsQuery,
} from 'src/redux/api'
import {
  resetGoogleApiState,
  setCity,
  setDistrict,
  setStreet,
} from 'src/redux/slices/googleApiSlice'
import { RootState } from 'src/redux/store'
import { skipToken } from '@reduxjs/toolkit/query'

interface ICreateRequestForm {
  title: string
  city: string
  propertyType: { value: string; label: string }
  listingType: { value: string; label: string }
  roomsFrom: { value: string; label: string }
  roomsTo: { value: string; label: string }
  areaFrom: number
  areaTo: number
  priceFrom: number
  priceTo: number
  currency: string
  //
  floorFrom: number
  floorTo: number
  cellingHeight: { label: string; value: string }[]
  yearOfConstruction: { label: string; value: string }[]
  availabilityOfFurniture: boolean
  furniture: { label: string; value: string }[]
  parking: { label: string; value: string }[]
  amenities: { label: string; value: string }[]
  petsAllowed: boolean
  forSmokers: boolean
  transport: { label: string; value: string }[]
  infrastructure: { label: string; value: string }[]
  windowView: { label: string; value: string }[]
  separateEntrance: boolean
  utilities: { label: string; value: string }[]
  minRentTerm: { label: string; value: string }
  energySavingTechnologies: { label: string; value: string }[]
  energyConsumptionClass: { label: string; value: string }
  newConstruction: boolean
  availabilityOfRepairs: { label: string; value: string }
  yard: { label: string; value: string }[]
  objectStatus: { label: string; value: string }
  documents: { label: string; value: string }[]
  security: { label: string; value: string }[]
  heating: { label: string; value: string }[]
  waterSupply: { label: string; value: string }[]
  sewerage: { label: string; value: string }[]
  floorType: { label: string; value: string }[]
  windowType: { label: string; value: string }[]
  luxuryRealEstate: { label: string; value: string }[]
  registrationPossibility: boolean
  priceIncludesUtilities: boolean
  bargaining: boolean
  priceIncludesRealtor: boolean
  trafficPerDayFrom: string
  trafficPerDayTo: string
  commercialRenovation: { label: string; value: string }[]
  redevelopmentPossibility: boolean
  freightTransportAvailability: boolean
  transportationAccess: { label: string; value: string }[]
  districtType: { label: string; value: string }
  naturalAreas: { label: string; value: string }[]
  nearIndustrialFacilities: boolean
  cleanAir: boolean
  quietArea: boolean
  noisyArea: boolean
  areaPrestigeLevel: { label: string; value: string }
  roadCondition: { label: string; value: string }[]
  streetLighting: boolean
  naturalFactorsRisk: { label: string; value: string }[]
  securityLevel: { label: string; value: string }
  windowOrientation: { label: string; value: string }[]
  planningType: { label: string; value: string }
  remoteWork: { label: string; value: string }[]
  provideReceipts: boolean
  earlyContractTermination: boolean
  leaseExtendingPossibility: boolean
  outbuildings: { label: string; value: string }[]
  garden: boolean
  startCattle: boolean
  profitableObject: boolean
  paymentInstallments: boolean
  commercialType?: { label: string; value: string }
  livingAreaFrom?: number
  livingAreaTo?: number
  furnitureAvailable?: boolean
  ownerOnly?: boolean
}

const getOptionValue = (
  option: { label: string; value: string } | { label: string; value: string }[],
) => {
  if (!option) {
    return
  }

  if (Array.isArray(option)) {
    return option.map((opt) => opt?.value?.toUpperCase())
  }

  return option?.value?.toUpperCase()
}

// Seed values for "save this search as a request" — built from the map/search
// filters. Types/deal are backend enum strings (PLOT, SALE…), location is a
// Google placeId resolved to place details internally.
export interface IRequestPrefill {
  propertyType?: string
  listingType?: string
  priceFrom?: number | string
  priceTo?: number | string
  cityPlaceId?: string
  // Custom search zone drawn on the map (J3) — sent as-is to the backend.
  coordinates?: number[][][]
}

interface ICreateRequestModal extends IMyModal {
  editId?: string
  prefill?: IRequestPrefill
}

const CreateRequestModal: FC<ICreateRequestModal> = ({ open, handleClose, editId, prefill }) => {
  const dispatch = useDispatch()
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const { city, district, street } = useSelector((state: RootState) => state.googleApi)
  const [locationError, setLocationError] = useState<null | string>(null)
  const [openSuccessModal, setOpenSuccessModal] = useState(false)
  const { t } = useTranslation()
  const [createPropertySearch, { error, isLoading: createPropertySearchLoading }] =
    useCreatePropertySearchMutation()
  const { data: editData } = useGetPropertySearchByIdQuery(
    open && editId ? editId : (skipToken as any),
  )

  // Validate that value looks like a Google place_id (not a place name)
  // Google place_ids are typically 20+ chars with alphanumeric/underscore/hyphen characters
  const isValidPlaceId = (value: string | undefined | null): boolean => {
    if (!value) return false
    return value.length > 15 && /^[A-Za-z0-9_-]+$/.test(value)
  }

  const settlementId = editData?.addressSection?.settlement
  const districtId = editData?.addressSection?.district
  const streetId = editData?.addressSection?.street

  // Only fetch place details if the stored value looks like a valid place_id
  const { data: cityDetails } = usePlaceDetailsQuery(
    settlementId && isValidPlaceId(settlementId) ? { place_id: settlementId } : (skipToken as any),
  )
  const { data: districtDetails } = usePlaceDetailsQuery(
    districtId && isValidPlaceId(districtId) ? { place_id: districtId } : (skipToken as any),
  )
  const { data: streetDetails } = usePlaceDetailsQuery(
    streetId && isValidPlaceId(streetId) ? { place_id: streetId } : (skipToken as any),
  )

  const {
    currencyOptions,
    cellingHeightOptions,
    yearOfConstructionOptions,
    furnitureOptions,
    parkingOptions,
    amenitiesOptions,
    transportOptions,
    infrastructureOptions,
    windowViewOptions,
    utilitiesOptions,
    minRentTermOptions,
    energySavingTechnologiesOptions,
    energyConsumptionClassOptions,
    repairsAvaliabilityOptions,
    yardOptions,
    objectStatusOptions,
    documentsOptions,
    securityOptions,
    heatingOptions,
    waterSupplyOptions,
    sewerageOptions,
    floorTypeOptions,
    windowTypeOptions,
    luxuryRealEstateOptions,
    commercialRenovationOptions,
    transportationAccessOptions,
    districtTypeOptions,
    naturalAreasOptions,
    areaPrestigeLevelOptions,
    roadConditionOptions,
    naturalFactorsRiskOptions,
    securityLevelOptions,
    windowOrientationOptions,
    planningTypeOptions,
    remoteWorkOptions,
    outbuildingsOptions,
  } = usePropertyOptions()

  const listingOptions = [
    { value: 'sale', label: t('step1.sale') },
    { value: 'rent_long_term', label: t('step1.rent-long-term') },
    { value: 'rent_daily', label: t('step1.rent-daily') },
    { value: 'rent_hourly', label: t('step1.rent-hourly') },
  ]

  const propertyTypeOptions = [
    { value: 'apartment', label: t('step1.apartment') },
    { value: 'house', label: t('step1.house') },
    { value: 'townhouse', label: t('step1.townhouse') },
    { value: 'duplex', label: t('step1.duplex') },
    { value: 'part_of_house', label: t('step1.partOfHouse') },
    { value: 'room', label: t('step1.room') },
    { value: 'commercial_property', label: t('step1.commercialProperty') },
    { value: 'plot', label: t('step1.plot') },
    { value: 'garage', label: t('step1.garage') },
  ]

  const roomOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5+', label: '5+' },
  ]

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<ICreateRequestForm>({
    defaultValues: {
      title: '',
      roomsFrom: roomOptions[0],
      roomsTo: roomOptions[roomOptions.length - 1],
      areaFrom: undefined,
      areaTo: undefined,
      priceFrom: undefined,
      priceTo: undefined,
      floorFrom: undefined,
      floorTo: undefined,
      livingAreaFrom: undefined,
      livingAreaTo: undefined,
      trafficPerDayFrom: '',
      trafficPerDayTo: '',
      availabilityOfFurniture: false,
      petsAllowed: false,
      forSmokers: false,
      separateEntrance: false,
      registrationPossibility: false,
      priceIncludesUtilities: false,
      bargaining: false,
      priceIncludesRealtor: false,
      redevelopmentPossibility: false,
      freightTransportAvailability: false,
      nearIndustrialFacilities: false,
      cleanAir: false,
      quietArea: false,
      noisyArea: false,
      streetLighting: false,
      newConstruction: false,
      provideReceipts: false,
      earlyContractTermination: false,
      leaseExtendingPossibility: false,
      garden: false,
      startCattle: false,
      profitableObject: false,
      paymentInstallments: false,
      ownerOnly: false,
    },
  })

  useEffect(() => {
    if (open && editData) {
      const toKey = (v: any) => {
        if (v == null) return ''
        if (typeof v === 'string') return v.toLowerCase()
        if (typeof v === 'number') return String(v).toLowerCase()
        if (Array.isArray(v)) return String(v[0] ?? '').toLowerCase()
        return String(v).toLowerCase()
      }
      const findOption = (opts: { value: string; label: string }[], v?: any) =>
        opts.find((o) => o.value.toLowerCase() === toKey(v)) || null
      const findMulti = (opts: { value: string; label: string }[], values?: any[]) =>
        (Array.isArray(values) ? values : []).map((v) => findOption(opts, v)).filter(Boolean) as any

      const listingTypeOpt = findOption(
        listingOptions,
        (editData.listingSection?.listingType || '').toLowerCase(),
      ) as any
      const propertyTypeOpt = findOption(
        propertyTypeOptions,
        (editData.listingSection?.propertyType || '').toLowerCase(),
      ) as any

      const p = (editData as any).parametersSection || {}
      const price = (editData as any).price || {}

      reset({
        title: editData.title,
        listingType: listingTypeOpt,
        propertyType: propertyTypeOpt,
        roomsFrom: findOption(roomOptions, p.roomsFrom) || roomOptions[0],
        roomsTo: findOption(roomOptions, p.roomsTo) || roomOptions[roomOptions.length - 1],
        areaFrom: p.areaFrom as any,
        areaTo: p.areaTo as any,
        floorFrom: p.floorFrom as any,
        floorTo: p.floorTo as any,
        livingAreaFrom: p.livingAreaFrom as any,
        livingAreaTo: p.livingAreaTo as any,
        priceFrom: price.amountFrom as any,
        priceTo: price.amountTo as any,
        currency: price.currency
          ? ({ value: price.currency, label: price.currency } as any)
          : undefined,
        availabilityOfFurniture: p.availabilityOfFurniture || false,
        petsAllowed: p.petsAllowed || false,
        forSmokers: p.forSmokers || false,
        separateEntrance: p.separateEntrance || false,
        registrationPossibility: p.registrationPossibility || false,
        priceIncludesUtilities: p.priceIncludesUtilities || false,
        bargaining: p.bargaining || false,
        priceIncludesRealtor: p.priceIncludesRealtor || false,
        redevelopmentPossibility: p.redevelopmentPossibility || false,
        freightTransportAvailability: p.freightTransportAvailability || false,
        nearIndustrialFacilities: p.nearIndustrialFacilities || false,
        cleanAir: p.cleanAir || false,
        quietArea: p.quietArea || false,
        noisyArea: p.noisyArea || false,
        streetLighting: p.streetLighting || false,
        newConstruction: p.newConstruction || false,
        provideReceipts: p.provideReceipts || false,
        earlyContractTermination: p.earlyContractTermination || false,
        leaseExtendingPossibility: p.leaseExtendingPossibility || false,
        garden: p.garden || false,
        startCattle: p.startCattle || false,
        profitableObject: p.profitableObject || false,
        paymentInstallments: p.paymentInstallments || false,
        ownerOnly: Array.isArray(editData.listingSection?.ownershipType)
          ? (editData.listingSection!.ownershipType as any[]).length === 1 &&
            (editData.listingSection!.ownershipType as any[])[0] === 'OWNER'
          : false,
        minRentTerm: findOption(minRentTermOptions, p.minRentTerm) as any,
        energyConsumptionClass: findOption(
          energyConsumptionClassOptions,
          p.energyConsumptionClass,
        ) as any,
        availabilityOfRepairs: findOption(
          repairsAvaliabilityOptions,
          p.availabilityOfRepairs,
        ) as any,
        objectStatus: findOption(objectStatusOptions, p.objectStatus) as any,
        documents: findOption(documentsOptions, p.documents) as any,
        districtType: findOption(districtTypeOptions, p.districtType) as any,
        areaPrestigeLevel: findOption(areaPrestigeLevelOptions, p.areaPrestigeLevel) as any,
        securityLevel: findOption(securityLevelOptions, p.securityLevel as any) as any,
        planningType: findOption(planningTypeOptions, p.planningType as any) as any,
        commercialType: findOption(
          [
            { value: 'retail_space', label: t('step1.retail_space') },
            { value: 'office_space', label: t('step1.office_space') },
            { value: 'warehouse_and_industrial', label: t('step1.warehouse_and_industrial') },
            { value: 'specialized', label: t('step1.specialized') },
          ],
          p.commercialType,
        ) as any,
        cellingHeight: findMulti(cellingHeightOptions, p.cellingHeight),
        yearOfConstruction: findMulti(yearOfConstructionOptions, p.yearOfConstruction),
        furniture: findMulti(furnitureOptions, p.furniture),
        parking: findMulti(parkingOptions, p.parking),
        amenities: findMulti(amenitiesOptions, p.amenities),
        transport: findMulti(transportOptions, p.transport),
        infrastructure: findMulti(infrastructureOptions, p.infrastructure),
        windowView: findMulti(windowViewOptions, p.windowView),
        utilities: findMulti(utilitiesOptions, p.utilities),
        energySavingTechnologies: findMulti(
          energySavingTechnologiesOptions,
          p.energySavingTechnologies,
        ),
        yard: findMulti(yardOptions, p.yard),
        security: findMulti(securityOptions, p.security),
        heating: findMulti(heatingOptions, p.heating),
        waterSupply: findMulti(waterSupplyOptions, p.waterSupply),
        sewerage: findMulti(sewerageOptions, p.sewerage),
        floorType: findMulti(floorTypeOptions, p.floorType),
        windowType: findMulti(windowTypeOptions, p.windowType),
        luxuryRealEstate: findMulti(luxuryRealEstateOptions, p.luxuryRealEstate),
        commercialRenovation: findOption(
          commercialRenovationOptions,
          Array.isArray(p.commercialRenovation)
            ? p.commercialRenovation[0]
            : p.commercialRenovation,
        ) as any,
        transportationAccess: findMulti(transportationAccessOptions, p.transportationAccess),
        naturalAreas: findMulti(naturalAreasOptions, p.naturalAreas),
        roadCondition: findMulti(roadConditionOptions, p.roadCondition),
        naturalFactorsRisk: findMulti(naturalFactorsRiskOptions, p.naturalFactorsRisk),
        windowOrientation: findMulti(windowOrientationOptions, p.windowOrientation),
        remoteWork: findMulti(remoteWorkOptions, p.remoteWork),
        outbuildings: findMulti(outbuildingsOptions, p.outbuildings),
      } as any)

      if (cityDetails) dispatch(setCity(cityDetails as any))
      if (districtDetails) dispatch(setDistrict(districtDetails as any))
      if (streetDetails) dispatch(setStreet(streetDetails as any))
    }
  }, [open, editData, cityDetails, districtDetails, streetDetails])

  // Prefill from the search filters ("save this search"): applies only when
  // creating (edit wins otherwise). The auto-title effect below then composes
  // the suggested name from these values for free.
  const { data: prefillCityDetails } = usePlaceDetailsQuery(
    open && !editId && prefill?.cityPlaceId && isValidPlaceId(prefill.cityPlaceId)
      ? { place_id: prefill.cityPlaceId }
      : (skipToken as any),
  )

  useEffect(() => {
    if (!open || editId || !prefill) return
    const lower = (v?: string) => (v || '').toLowerCase()
    const listingOpt = listingOptions.find((o) => o.value === lower(prefill.listingType))
    const propertyOpt = propertyTypeOptions.find((o) => o.value === lower(prefill.propertyType))
    if (listingOpt) setValue('listingType', listingOpt as any)
    if (propertyOpt) setValue('propertyType', propertyOpt as any)
    if (prefill.priceFrom) setValue('priceFrom', Number(prefill.priceFrom) as any)
    if (prefill.priceTo) setValue('priceTo', Number(prefill.priceTo) as any)
  }, [open, editId, prefill])

  useEffect(() => {
    if (open && !editId && prefillCityDetails) {
      dispatch(setCity(prefillCityDetails as any))
    }
  }, [open, editId, prefillCityDetails, dispatch])

  const closeModals = () => {
    setOpenSuccessModal(false)
    handleClose()
  }

  // Wheel anywhere over the modal — banner, buttons, side gutters — drives the
  // form scroll: the owner scrolled at the modal's right edge and nothing
  // moved (feedback 2026-08-21). Inside the area itself (or an open select
  // menu) native scrolling stays untouched.
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const forwardWheel = (e: WheelEvent<HTMLDivElement>) => {
    const area = scrollAreaRef.current
    const target = e.target as Element
    // Select menus are portaled to <body> inside modals, yet their React
    // events still bubble here — their own list must keep scrolling.
    if (!area || area.contains(target) || target.closest?.('.my-select__menu')) return
    area.scrollTop += e.deltaY
  }

  const onSubmit = async (data: ICreateRequestForm) => {
    if (!city) {
      setLocationError('validation.required')
      return
    }

    try {
      const {
        roomsFrom,
        roomsTo,
        floorFrom,
        floorTo,
        areaFrom,
        areaTo,
        livingAreaFrom,
        livingAreaTo,
        priceFrom,
        priceTo,
        currency,
        ownerOnly,
        ...rest
      } = data

      // Only the parameters relevant to the chosen property type reach the
      // payload: sending the default rooms range for a land-plot request makes
      // matching filter on `rooms` — a field plot listings never have.
      const parametersSection: Record<string, unknown> = {
        roomsFrom: showRoomsRequest ? getOptionValue(roomsFrom) : undefined,
        roomsTo: showRoomsRequest ? getOptionValue(roomsTo) : undefined,
        floorFrom: showFloorRequest && floorFrom ? Number(floorFrom) : undefined,
        floorTo: showFloorRequest && floorTo ? Number(floorTo) : undefined,
        areaFrom: areaFrom ? Number(areaFrom) : undefined,
        areaTo: areaTo ? Number(areaTo) : undefined,
        livingAreaFrom:
          showLivingAreaRequest && livingAreaFrom ? Number(livingAreaFrom) : undefined,
        livingAreaTo: showLivingAreaRequest && livingAreaTo ? Number(livingAreaTo) : undefined,
        trafficPerDayFrom: (rest as any)?.trafficPerDayFrom
          ? Number((rest as any).trafficPerDayFrom)
          : undefined,
        trafficPerDayTo: (rest as any)?.trafficPerDayTo
          ? Number((rest as any).trafficPerDayTo)
          : undefined,
        availabilityOfFurniture: (rest as any).availabilityOfFurniture || false,
        cellingHeight: getOptionValue((rest as any).cellingHeight),
        yearOfConstruction: getOptionValue((rest as any).yearOfConstruction),
        furniture: getOptionValue((rest as any).furniture),
        parking: getOptionValue((rest as any).parking),
        amenities: getOptionValue((rest as any).amenities),
        petsAllowed: (rest as any).petsAllowed || false,
        forSmokers: (rest as any).forSmokers || false,
        transport: getOptionValue((rest as any).transport),
        infrastructure: getOptionValue((rest as any).infrastructure),
        windowView: getOptionValue((rest as any).windowView),
        separateEntrance: (rest as any).separateEntrance || false,
        utilities: getOptionValue((rest as any).utilities),
        minRentTerm: getOptionValue((rest as any).minRentTerm),
        energySavingTechnologies: getOptionValue((rest as any).energySavingTechnologies),
        energyConsumptionClass: getOptionValue((rest as any).energyConsumptionClass),
        newConstruction: (rest as any).newConstruction || false,
        availabilityOfRepairs: getOptionValue((rest as any).availabilityOfRepairs),
        yard: getOptionValue((rest as any).yard),
        objectStatus: getOptionValue((rest as any).objectStatus),
        documents: getOptionValue((rest as any).documents),
        registrationPossibility: (rest as any).registrationPossibility || false,
        priceIncludesUtilities: (rest as any).priceIncludesUtilities || false,
        bargaining: (rest as any).bargaining || false,
        security: getOptionValue((rest as any).security),
        heating: getOptionValue((rest as any).heating),
        waterSupply: getOptionValue((rest as any).waterSupply),
        sewerage: getOptionValue((rest as any).sewerage),
        floorType: getOptionValue((rest as any).floorType),
        windowType: getOptionValue((rest as any).windowType),
        luxuryRealEstate: getOptionValue((rest as any).luxuryRealEstate),
        priceIncludesRealtor: (rest as any).priceIncludesRealtor || false,
        commercialRenovation: getOptionValue((rest as any).commercialRenovation),
        transportationAccess: getOptionValue((rest as any).transportationAccess),
        districtType: getOptionValue((rest as any).districtType),
        naturalAreas: getOptionValue((rest as any).naturalAreas),
        nearIndustrialFacilities: (rest as any).nearIndustrialFacilities || false,
        cleanAir: (rest as any).cleanAir || false,
        quietArea: (rest as any).quietArea || false,
        noisyArea: (rest as any).noisyArea || false,
        areaPrestigeLevel: getOptionValue((rest as any).areaPrestigeLevel),
        roadCondition: getOptionValue((rest as any).roadCondition),
        streetLighting: (rest as any).streetLighting || false,
        naturalFactorsRisk: getOptionValue((rest as any).naturalFactorsRisk),
        securityLevel: getOptionValue((rest as any).securityLevel),
        windowOrientation: getOptionValue((rest as any).windowOrientation),
        planningType: getOptionValue((rest as any).planningType),
        remoteWork: getOptionValue((rest as any).remoteWork),
        provideReceipts: (rest as any).provideReceipts || false,
        earlyContractTermination: (rest as any).earlyContractTermination || false,
        leaseExtendingPossibility: (rest as any).leaseExtendingPossibility || false,
        outbuildings: getOptionValue((rest as any).outbuildings),
        garden: (rest as any).garden || false,
        startCattle: (rest as any).startCattle || false,
        profitableObject: (rest as any).profitableObject || false,
        paymentInstallments: (rest as any).paymentInstallments || false,
        // the kind of commercial property the seeker wants (shown above, never sent before)
        commercialType: getOptionValue((rest as any).commercialType),
      }

      const hiddenRequestParamKeys = [
        ...(!showBuildingParamsRequest ? ['cellingHeight', 'yearOfConstruction'] : []),
        ...(!showComfortRequest
          ? ['availabilityOfFurniture', 'furniture', 'parking', 'amenities', 'windowView']
          : []),
        ...(!isResidentialRequest ? ['petsAllowed', 'forSmokers'] : []),
        ...(!showSeparateEntranceRequest ? ['separateEntrance'] : []),
        ...(!showEcoEnergyRequest ? ['energySavingTechnologies', 'energyConsumptionClass'] : []),
        ...(!showRepairsRequest ? ['newConstruction', 'availabilityOfRepairs', 'yard'] : []),
        ...(!showHeatingRequest ? ['heating'] : []),
        ...(!showInteriorFinishRequest ? ['floorType', 'windowType'] : []),
        ...(!showLuxuryRequest ? ['luxuryRealEstate'] : []),
        ...(!showRentOptionsRequest ? ['registrationPossibility', 'priceIncludesUtilities'] : []),
        ...(!showCommercialParamsRequest
          ? [
              'commercialType',
              'redevelopmentPossibility',
              'freightTransportAvailability',
              'trafficPerDayFrom',
              'trafficPerDayTo',
              'commercialRenovation',
            ]
          : []),
        ...(!showPlanningRequest ? ['windowOrientation', 'planningType'] : []),
        ...(!showRemoteWorkRequest ? ['remoteWork'] : []),
        ...(!isRentRequest
          ? [
              'provideReceipts',
              'earlyContractTermination',
              'leaseExtendingPossibility',
              'minRentTerm',
            ]
          : []),
        ...(!showRuralRequest ? ['outbuildings', 'garden', 'startCattle'] : []),
      ]
      hiddenRequestParamKeys.forEach((key) => delete parametersSection[key])

      await createPropertySearch({
        ...(editId ? { id: editId } : {}),
        title: data.title,
        ownershipType: ownerOnly ? ['OWNER'] : ['OWNER', 'REALTOR'],
        propertyType: (data as any).propertyType?.value?.toUpperCase(),
        listingType: (data as any).listingType?.value?.toUpperCase(),
        price: {
          amountFrom: Number(priceFrom),
          amountTo: Number(priceTo),
          currency: (currency as any)?.value || currency,
        },
        address: {
          settlement: city,
          district: district || undefined,
          street: street || undefined,
        },
        // A zone drawn on the map (J3) is used verbatim as the search area;
        // without it the backend geocodes the address.
        ...(prefill?.coordinates ? { coordinates: prefill.coordinates } : {}),
        parametersSection,
      }).unwrap()
      reset()
      setOpenSuccessModal(true)
    } catch {
      // error toast is shown by the global toast middleware
    }
  }

  const commercialTypeOptions = [
    { value: 'retail_space', label: t('step1.retail_space') },
    { value: 'office_space', label: t('step1.office_space') },
    { value: 'warehouse_and_industrial', label: t('step1.warehouse_and_industrial') },
    { value: 'specialized', label: t('step1.specialized') },
  ]
  const {
    propertyType,
    availabilityOfFurniture,
    listingType,
    noisyArea: isNoisy,
    quietArea: isQueit,
  } = watch()

  // Per-type field visibility, mirroring the create-ad wizard: a request for a
  // land plot must not ask about rooms, floors or furniture — and must not
  // send those defaults to matching, where `rooms >= 1` silently excludes
  // every plot listing (plots have no rooms at all).
  const requestType = (propertyType as any)?.value?.toUpperCase() || ''
  const isPlotRequest = requestType === 'PLOT'
  const isGarageRequest = requestType === 'GARAGE'
  const isCommercialRequest = requestType === 'COMMERCIAL_PROPERTY'
  const isFlatLikeRequest = ['APARTMENT', 'PART_OF_HOUSE', 'ROOM'].includes(requestType)
  const isResidentialRequest =
    !requestType ||
    ['APARTMENT', 'HOUSE', 'TOWNHOUSE', 'DUPLEX', 'PART_OF_HOUSE', 'ROOM'].includes(requestType)
  const isRentRequest = String((listingType as any)?.value || '').startsWith('rent')
  const showRoomsRequest = isResidentialRequest
  const showFloorRequest = !requestType || isFlatLikeRequest
  const showLivingAreaRequest = isResidentialRequest
  const showBuildingParamsRequest = !isPlotRequest // cellingHeight, yearOfConstruction
  const showComfortRequest = isResidentialRequest || isCommercialRequest // furniture, parking, amenities, windowView
  const showEcoEnergyRequest = isResidentialRequest || isCommercialRequest
  const showRepairsRequest = isResidentialRequest || isCommercialRequest // newConstruction, repairs, yard
  const showHeatingRequest = !isPlotRequest
  const showInteriorFinishRequest = isResidentialRequest || isCommercialRequest // floorType, windowType
  const showLuxuryRequest = isResidentialRequest
  const showRentOptionsRequest = isRentRequest && isResidentialRequest
  const showCommercialParamsRequest = isCommercialRequest
  const showPlanningRequest = isResidentialRequest // windowOrientation, planningType
  const showRemoteWorkRequest = isResidentialRequest || isCommercialRequest
  const showRuralRequest =
    !requestType || ['HOUSE', 'TOWNHOUSE', 'DUPLEX', 'PART_OF_HOUSE', 'PLOT'].includes(requestType)
  const showSeparateEntranceRequest = !isPlotRequest && !isGarageRequest

  // The title exists purely for the user's own convenience; most people left
  // it cryptic or empty, so suggest one from the picked parameters until the
  // user edits the field manually (roadmap 16.5).
  useEffect(() => {
    if (editData || dirtyFields.title) return
    const parts = [
      (propertyType as any)?.label,
      (listingType as any)?.label,
      (city as any)?.name,
    ].filter(Boolean)
    if (parts.length) setValue('title', parts.join(', '))
  }, [propertyType, listingType, city, editData])

  useEffect(() => {
    if (!open) {
      reset()
      dispatch(resetGoogleApiState())
      setIsExpanded(false)
    }
  }, [open])

  return (
    <MyModal
      showClose={false}
      overflowAuto={false}
      width="w-[calc(100%_-_2rem)] max-w-[640px]"
      open={open}
      handleClose={handleClose}
    >
      <div className="min-h-[300px] max-w-[620px]" onWheel={forwardWheel}>
        <button
          onClick={() => handleClose()}
          className="absolute z-20 top-3.5 right-3.5 w-11 h-11 flex items-center justify-center"
          aria-label={t('close')}
        >
          <span className="icon-button fill-white hover:fill-danger-strong w-4 h-4">
            <CloseIcon />
          </span>
        </button>
        <div className="flex flex-col gap-4">
          {/* On phones the banner shrinks to a slim title strip — a 243px
              hero image plus the form did not fit 80vh (39.4) */}
          <div className="relative rounded-lg overflow-hidden flex flex-col items-center justify-center p-4 py-6 text-white bg-black md:min-h-[15.1875rem]">
            <div className="flex flex-col text-center gap-[.6875rem] relative z-10">
              <h2 className="font-black text-lg leading-[1.1069rem]">
                {t('create_your_housing_search_request')}
              </h2>
              <div className="text-base font-semibold">
                {t('it_takes_approximately_20_seconds')}
              </div>
            </div>

            <div className="absolute inset-0 opacity-60">
              <img src={banner} alt="" className="object-cover size-full" />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              {/* Always scrollable: on 390 even the collapsed fields stack past
                  400px and used to spill over the action buttons (39.4).
                  -mx-4 stretches the scroll container to the modal edge (MyModal
                  p-4), so the wheel works over the right gutter and the scrollbar
                  sits at the edge the user grabs (owner feedback 2026-08-21). */}
              <div
                ref={scrollAreaRef}
                className="max-h-[40vh] md:max-h-[400px] -mx-4 px-4 flex flex-col gap-4 overflow-auto"
              >
                {error && (
                  <div className="text-danger text-sm">{(error as any)?.data?.message}</div>
                )}

                <div className="flex md:flex-row flex-col gap-2 md:items-start">
                  <Controller
                    control={control}
                    rules={{ required: t('request_title_is_required') }}
                    name="title"
                    render={({ field, fieldState }) => (
                      <InputField
                        {...field}
                        className="w-full flex-1"
                        label={t('request_title')}
                        placeholder={t('request_title_placeholder')}
                        error={fieldState.error}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="listingType"
                    rules={{ required: t('listing_type_is_required') }}
                    render={({ field, fieldState }) => {
                      return (
                        <MySelect
                          {...field}
                          wrapperClassName="w-full flex-1"
                          isDefault
                          label={t('step1.listingType')}
                          placeholder={t('step1.rent-long-term')}
                          options={listingOptions}
                          error={fieldState.error}
                        />
                      )
                    }}
                  />
                </div>
                <div className="flex md:flex-row flex-col gap-2 md:items-start">
                  <div className="flex flex-col flex-1 w-full">
                    <AutocompleteCustom
                      label={t('step1.settlement')}
                      placeholder={t('step1.settlement')}
                      error={
                        locationError ? { type: 'required', message: t(locationError) } : undefined
                      }
                      reduxValue={city}
                      className="w-full"
                      onPlaceSelect={(place) => {
                        dispatch(setCity(place))
                        setLocationError(null)
                      }}
                      search={['locality']}
                    />
                  </div>

                  <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                    <Controller
                      control={control}
                      name="propertyType"
                      rules={{ required: t('property_type_is_required') }}
                      render={({ field, fieldState }) => {
                        return (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('property_type')}
                            placeholder={t('apartment')}
                            options={propertyTypeOptions}
                            error={fieldState.error}
                          />
                        )
                      }}
                    />
                    {(propertyType as any)?.value === 'commercial_property' && (
                      <Controller
                        control={control}
                        name="commercialType"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step1.commercialType')}
                            placeholder={commercialTypeOptions[0]?.label}
                            options={commercialTypeOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className="flex md:flex-row flex-col gap-2 md:items-start">
                    <div className="flex flex-col flex-1">
                      <AutocompleteCustom
                        label={t('step1.district')}
                        placeholder={t('step1.selectDistrict')}
                        disabled={!city}
                        reduxValue={district}
                        onPlaceSelect={(place) => dispatch(setDistrict(place))}
                        search={['sublocality']}
                        bounds={city?.geometry?.viewport}
                      />
                    </div>
                    <div className="flex flex-col flex-1">
                      <AutocompleteCustom
                        label={t('step1.street')}
                        placeholder={t('step1.street')}
                        disabled={!city}
                        reduxValue={street}
                        onPlaceSelect={(place) => dispatch(setStreet(place))}
                        search={['route']}
                        bounds={district?.geometry?.viewport || city?.geometry?.viewport}
                      />
                    </div>
                  </div>
                )}

                <div className="flex md:flex-row flex-col gap-2 md:items-start">
                  {showRoomsRequest && (
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="roomsFrom"
                        render={({ field }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={`${t('rooms')} (${t('from')})`}
                            placeholder="1"
                            options={roomOptions}
                            error={errors.roomsFrom as any}
                          />
                        )}
                      />
                      <div aria-hidden="true" className="hidden md:flex self-end mb-3">
                        -
                      </div>
                      <Controller
                        control={control}
                        name="roomsTo"
                        render={({ field }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={`${t('rooms')} (${t('to')})`}
                            placeholder="1"
                            options={roomOptions}
                            error={errors.roomsTo as any}
                          />
                        )}
                      />
                    </div>
                  )}
                  <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                    <Controller
                      control={control}
                      name="priceFrom"
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <InputField
                          {...field}
                          className="w-full flex-1"
                          type="number"
                          label={`${t('price')} (${t('from')})`}
                          placeholder="15 000"
                          error={errors.priceFrom}
                        />
                      )}
                    />
                    <div aria-hidden="true" className="hidden md:flex self-end mb-3">
                      -
                    </div>
                    <Controller
                      control={control}
                      rules={{ required: t('validation.required') }}
                      name="priceTo"
                      render={({ field }) => (
                        <InputField
                          {...field}
                          className="w-full flex-1"
                          type="number"
                          label={`${t('price')} (${t('to')})`}
                          placeholder="20 000"
                          error={errors.priceTo}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="currency"
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <MySelect
                          {...field}
                          wrapperClassName="w-full flex-1"
                          isDefault
                          label={t('step3.currency')}
                          placeholder="UAH"
                          options={currencyOptions}
                          error={errors.currency}
                        />
                      )}
                    />
                  </div>
                </div>
                {isExpanded && (
                  <>
                    <div className="flex md:flex-row flex-col gap-2 md:items-start">
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                          <Controller
                            control={control}
                            name="areaFrom"
                            render={({ field, fieldState }) => (
                              <InputField
                                {...field}
                                label={`${t('step3.totalArea')} (${t('from')})`}
                                placeholder={t('step3.square_meter')}
                                type="number"
                                error={fieldState.error}
                              />
                            )}
                          />
                          <div aria-hidden="true" className="hidden md:flex self-end mb-3">
                            -
                          </div>

                          <Controller
                            control={control}
                            name="areaTo"
                            render={({ field, fieldState }) => (
                              <InputField
                                {...field}
                                label={`${t('step3.totalArea')} (${t('to')})`}
                                placeholder={t('step3.square_meter')}
                                type="number"
                                error={fieldState.error}
                              />
                            )}
                          />
                        </div>
                        {showFloorRequest && (
                          <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                            <Controller
                              control={control}
                              name="floorFrom"
                              render={({ field, fieldState }) => (
                                <InputField
                                  {...field}
                                  label={`${t('step3.floor')} (${t('from')})`}
                                  placeholder={t('step3.floor')}
                                  type="number"
                                  error={fieldState.error}
                                />
                              )}
                            />
                            <div aria-hidden="true" className="hidden md:flex self-end mb-3">
                              -
                            </div>

                            <Controller
                              control={control}
                              name="floorTo"
                              render={({ field, fieldState }) => (
                                <InputField
                                  {...field}
                                  label={`${t('step3.floor')} (${t('to')})`}
                                  placeholder={t('step3.floor')}
                                  type="number"
                                  error={fieldState.error}
                                />
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      {showLivingAreaRequest && (
                        <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                          <Controller
                            control={control}
                            name="livingAreaFrom"
                            render={({ field, fieldState }) => (
                              <InputField
                                {...field}
                                label={`${t('step3.livingArea')} (${t('from')})`}
                                placeholder={t('step3.square_meter')}
                                type="number"
                                error={fieldState.error}
                              />
                            )}
                          />
                          <div aria-hidden="true" className="hidden md:flex self-end mb-3">
                            -
                          </div>

                          <Controller
                            control={control}
                            name="livingAreaTo"
                            render={({ field, fieldState }) => (
                              <InputField
                                {...field}
                                label={`${t('step3.livingArea')} (${t('to')})`}
                                placeholder={t('step3.square_meter')}
                                type="number"
                                error={fieldState.error}
                              />
                            )}
                          />
                        </div>
                      )}
                      {showBuildingParamsRequest && (
                        <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                          <Controller
                            control={control}
                            name="cellingHeight"
                            render={({ field, fieldState }) => (
                              <MySelect
                                {...field}
                                isDefault
                                isMulti
                                wrapperClassName="w-full flex-1"
                                label={t('step3.cellingHeight')}
                                placeholder={cellingHeightOptions[0].label}
                                options={cellingHeightOptions}
                                error={fieldState.error}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name="yearOfConstruction"
                            render={({ field, fieldState }) => (
                              <MySelect
                                {...field}
                                isDefault
                                isMulti
                                wrapperClassName="w-full flex-1"
                                label={t('step3.yearOfConstruction')}
                                placeholder={yearOfConstructionOptions[0].label}
                                options={yearOfConstructionOptions}
                                error={fieldState.error}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                    {/* */}

                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="ownerOnly"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.owner')}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {isResidentialRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="petsAllowed"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.petsAllowed')}
                              />
                            )}
                          />
                        </div>
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="forSmokers"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.forSmokers')}
                              />
                            )}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      {showRepairsRequest && (
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="newConstruction"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.newConstruction')}
                              />
                            )}
                          />
                        </div>
                      )}
                      {showSeparateEntranceRequest && (
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="separateEntrance"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.separateEntrance')}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                    {showRentOptionsRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="registrationPossibility"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.registrationPossibility')}
                              />
                            )}
                          />
                        </div>
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="priceIncludesUtilities"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.priceIncludesUtilities')}
                              />
                            )}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="bargaining"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.bargaining')}
                            />
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="priceIncludesRealtor"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.priceIncludesRealtor')}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {showCommercialParamsRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="redevelopmentPossibility"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.redevelopmentPossibility')}
                              />
                            )}
                          />
                        </div>
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="freightTransportAvailability"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.freightTransportAvailability')}
                              />
                            )}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="nearIndustrialFacilities"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.nearIndustrialFacilities')}
                            />
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="cleanAir"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.cleanAir')}
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <div className={`flex-1 ${isNoisy ? 'opacity-50' : ''}`}>
                        <Controller
                          disabled={isNoisy}
                          control={control}
                          name="quietArea"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.quietArea')}
                            />
                          )}
                        />
                      </div>
                      <div className={`flex-1 ${isQueit ? 'opacity-50' : ''}`}>
                        <Controller
                          control={control}
                          name="noisyArea"
                          disabled={isQueit}
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.noisyArea')}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      {showComfortRequest && (
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="availabilityOfFurniture"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                label={t('step3.availabilityOfFurnitureLabel')}
                              />
                            )}
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="streetLighting"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.streetLighting')}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {showComfortRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="furniture"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              disabled={!availabilityOfFurniture}
                              isMulti={true}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.furnitureLabel')}
                              placeholder={furnitureOptions[0].label}
                              options={furnitureOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="parking"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.parking')}
                              placeholder={parkingOptions[0].label}
                              options={parkingOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      {showComfortRequest && (
                        <Controller
                          control={control}
                          name="amenities"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti={true}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.amenities')}
                              placeholder={amenitiesOptions[0].label}
                              options={amenitiesOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      )}
                      <Controller
                        control={control}
                        name="transport"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti={true}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.transport')}
                            placeholder={transportOptions[0].label}
                            options={transportOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="infrastructure"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti={true}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.infrastructure')}
                            placeholder={infrastructureOptions[0].label}
                            options={infrastructureOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                      {showComfortRequest && (
                        <Controller
                          control={control}
                          name="windowView"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti={true}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.windowView')}
                              placeholder={windowViewOptions[0].label}
                              options={windowViewOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      )}
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="utilities"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti={true}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.utilities')}
                            placeholder={utilitiesOptions[0].label}
                            options={utilitiesOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        {listingType?.value === 'rent_long_term' && (
                          <Controller
                            control={control}
                            name="minRentTerm"
                            render={({ field, fieldState }) => (
                              <MySelect
                                {...field}
                                wrapperClassName="w-full flex-1"
                                isDefault
                                label={t('step3.minRentTerm')}
                                placeholder={minRentTermOptions[0].label}
                                options={minRentTermOptions}
                                error={fieldState.error}
                              />
                            )}
                          />
                        )}
                      </div>
                    </div>
                    {showEcoEnergyRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="energyConsumptionClass"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.energyConsumptionClass')}
                              placeholder={energyConsumptionClassOptions[0].label}
                              options={energyConsumptionClassOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="energySavingTechnologies"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti={true}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.energySavingTechnologies')}
                              placeholder={energySavingTechnologiesOptions[0].label}
                              options={energySavingTechnologiesOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}

                    {showRepairsRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="availabilityOfRepairs"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.availabilityOfRepairs')}
                              placeholder={repairsAvaliabilityOptions[0].label}
                              options={repairsAvaliabilityOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="yard"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.yard')}
                              placeholder={yardOptions[0].label}
                              options={yardOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="objectStatus"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.objectStatus')}
                            placeholder={objectStatusOptions[0].label}
                            options={objectStatusOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="documents"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.documents')}
                            placeholder={documentsOptions[0].label}
                            options={documentsOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="security"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti={true}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.security')}
                            placeholder={securityOptions[0].label}
                            options={securityOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      {showHeatingRequest && (
                        <Controller
                          control={control}
                          name="heating"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.heating')}
                              placeholder={heatingOptions[0].label}
                              options={heatingOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      )}
                      <Controller
                        control={control}
                        name="waterSupply"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.waterSupply')}
                            placeholder={waterSupplyOptions[0].label}
                            options={waterSupplyOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="sewerage"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.sewerage')}
                            placeholder={sewerageOptions[0].label}
                            options={sewerageOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                      {showInteriorFinishRequest && (
                        <Controller
                          control={control}
                          name="floorType"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti={true}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.floorType')}
                              placeholder={floorTypeOptions[0].label}
                              options={floorTypeOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      )}
                    </div>
                    {showInteriorFinishRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="windowType"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti={true}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.windowType')}
                              placeholder={windowTypeOptions[0].label}
                              options={windowTypeOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}
                    {showLuxuryRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="luxuryRealEstate"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti={true}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.luxuryRealEstate')}
                              placeholder={luxuryRealEstateOptions[0].label}
                              options={luxuryRealEstateOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}

                    {showCommercialParamsRequest && (
                      <>
                        <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                          <Controller
                            control={control}
                            name="trafficPerDayFrom"
                            render={({ field, fieldState }) => (
                              <InputField
                                {...field}
                                label={`${t('step3.trafficPerDay')} (${t('from')})`}
                                placeholder="1500"
                                type="number"
                                error={fieldState.error}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name="trafficPerDayTo"
                            render={({ field, fieldState }) => (
                              <InputField
                                {...field}
                                label={`${t('step3.trafficPerDay')} (${t('to')})`}
                                placeholder="2000"
                                type="number"
                                error={fieldState.error}
                              />
                            )}
                          />
                        </div>

                        <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                          <Controller
                            control={control}
                            name="commercialRenovation"
                            render={({ field, fieldState }) => (
                              <MySelect
                                {...field}
                                isMulti={false}
                                wrapperClassName="w-full flex-1"
                                isDefault
                                label={t('step3.commercialRenovation')}
                                placeholder={commercialRenovationOptions[0].label}
                                options={commercialRenovationOptions}
                                error={fieldState.error}
                              />
                            )}
                          />
                        </div>
                      </>
                    )}

                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="transportationAccess"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti={true}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.transportationAccess')}
                            placeholder={transportationAccessOptions[0].label}
                            options={transportationAccessOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                      <Controller
                        control={control}
                        name="districtType"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.districtType')}
                            placeholder={districtTypeOptions[0].label}
                            options={districtTypeOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>

                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="naturalAreas"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti={true}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.naturalAreas')}
                            placeholder={naturalAreasOptions[0].label}
                            options={naturalAreasOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>

                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="areaPrestigeLevel"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.areaPrestigeLevel')}
                            placeholder={areaPrestigeLevelOptions[0].label}
                            options={areaPrestigeLevelOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="roadCondition"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti={true}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.roadCondition')}
                            placeholder={roadConditionOptions[0].label}
                            options={roadConditionOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="naturalFactorsRisk"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            isMulti
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.naturalFactorsRisk')}
                            placeholder={naturalFactorsRiskOptions[0].label}
                            options={naturalFactorsRiskOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <Controller
                        control={control}
                        name="securityLevel"
                        render={({ field, fieldState }) => (
                          <MySelect
                            {...field}
                            wrapperClassName="w-full flex-1"
                            isDefault
                            label={t('step3.securityLevel')}
                            placeholder={securityLevelOptions[0].label}
                            options={securityLevelOptions}
                            error={fieldState.error}
                          />
                        )}
                      />
                    </div>
                    {showPlanningRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="windowOrientation"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.windowOrientation')}
                              placeholder={windowOrientationOptions[0].label}
                              options={windowOrientationOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="planningType"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.planningType')}
                              placeholder={planningTypeOptions[0].label}
                              options={planningTypeOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}
                    {showRemoteWorkRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="remoteWork"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              isMulti
                              wrapperClassName="w-full flex-1"
                              isDefault
                              label={t('step3.forRemoteWork')}
                              placeholder={remoteWorkOptions[0].label}
                              options={remoteWorkOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}
                    {showRuralRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <Controller
                          control={control}
                          name="outbuildings"
                          render={({ field, fieldState }) => (
                            <MySelect
                              {...field}
                              wrapperClassName="w-full flex-1"
                              isDefault
                              isMulti
                              label={t('step3.outbuildings')}
                              placeholder={outbuildingsOptions[0].label}
                              options={outbuildingsOptions}
                              error={fieldState.error}
                            />
                          )}
                        />
                      </div>
                    )}
                    {isRentRequest && (
                      <>
                        <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                          <div className="flex-1">
                            <Controller
                              control={control}
                              name="provideReceipts"
                              render={({ field }) => (
                                <Checkbox
                                  {...(field as any)}
                                  checked={field.value}
                                  label={t('step3.provideReceipts')}
                                />
                              )}
                            />
                          </div>
                          <div className="flex-1">
                            <Controller
                              control={control}
                              name="earlyContractTermination"
                              render={({ field }) => (
                                <Checkbox
                                  {...(field as any)}
                                  checked={field.value}
                                  label={t('step3.earlyContractTermination')}
                                />
                              )}
                            />
                          </div>
                        </div>
                        <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                          <div className="flex-1">
                            <Controller
                              control={control}
                              name="leaseExtendingPossibility"
                              render={({ field }) => (
                                <Checkbox
                                  {...(field as any)}
                                  checked={field.value}
                                  label={t('step3.leaseExtendingPossibility')}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {showRuralRequest && (
                      <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="garden"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.garden')}
                              />
                            )}
                          />
                        </div>
                        <div className="flex-1">
                          <Controller
                            control={control}
                            name="startCattle"
                            render={({ field }) => (
                              <Checkbox
                                {...(field as any)}
                                checked={field.value}
                                label={t('step3.startCattle')}
                              />
                            )}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1">
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="profitableObject"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.profitableObject')}
                            />
                          )}
                        />
                      </div>
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="paymentInstallments"
                          render={({ field }) => (
                            <Checkbox
                              {...(field as any)}
                              checked={field.value}
                              label={t('step3.paymentInstallments')}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex md:flex-row flex-col gap-2 md:items-end flex-1"></div>
                  </>
                )}
              </div>
              <div className="flex md:items-center gap-4 md:flex-row flex-col md:justify-center">
                <Button
                  className="!h-11 md:min-w-[12.625rem] md:w-auto w-full"
                  variant={'outlined'}
                  iconAfter={
                    !isExpanded ? (
                      <ChevronDownIcon />
                    ) : (
                      <ChevronDownIcon style={{ transform: 'rotate(180deg)' }} />
                    )
                  }
                  onClick={(e) => {
                    e.preventDefault()
                    setIsExpanded((prev) => !prev)
                  }}
                >
                  {t('advanced_filters')}
                </Button>
                <Button
                  isLoading={createPropertySearchLoading}
                  type="submit"
                  className="!h-11 md:w-auto w-full"
                  iconAfter={<PlusIcon />}
                >
                  {t(editId ? 'save' : 'create_request')}
                </Button>
              </div>
            </form>
          </div>
          <SuccessModal
            title={t('request_successfully_created')}
            open={openSuccessModal}
            handleClose={closeModals}
          />
        </div>
      </div>
    </MyModal>
  )
}

export default CreateRequestModal
